import PropTypes from "prop-types"
import React, { Component } from "react"
import moment from "moment"

import Items from "./items/Items"
import InfoLabel from "./layout/InfoLabel"
import Sidebar from "./layout/Sidebar"
import Header from "./layout/Header"
import Columns from "./columns/Columns"
import GroupRows from "./row/GroupRows"
import ScrollElement from "./scroll/ScrollElement"
import MarkerCanvas from "./markers/MarkerCanvas"

import windowResizeDetector from "../resize-detector/window"

import {
  getMinUnit,
  getNextUnit,
  stack,
  nostack,
  layers,
  calculateDimensions,
  getGroupOrders,
  getVisibleItems,
} from "./utility/calendar"
import { _get, _length } from "./utility/generic"
import {
  defaultKeys,
  defaultTimeSteps,
  defaultHeaderLabelFormats,
  defaultSubHeaderLabelFormats,
} from "./default-config"
import { TimelineStateProvider } from "./timeline/TimelineStateContext"
import { TimelineMarkersProvider } from "./markers/TimelineMarkersContext"

export default class ReactCalendarTimeline extends Component {
  static propTypes = {
    groups: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    sidebarWidth: PropTypes.number,
    sidebarContent: PropTypes.node,
    rightSidebarWidth: PropTypes.number,
    rightSidebarContent: PropTypes.node,
    dragSnap: PropTypes.number,
    minResizeWidth: PropTypes.number,
    stickyOffset: PropTypes.number,
    stickyHeader: PropTypes.bool,
    lineHeight: PropTypes.number,
    headerLabelGroupHeight: PropTypes.number,
    headerLabelHeight: PropTypes.number,
    itemHeightRatio: PropTypes.number,

    minZoom: PropTypes.number,
    maxZoom: PropTypes.number,

    clickTolerance: PropTypes.number,

    canChangeGroup: PropTypes.bool,
    canMove: PropTypes.bool,
    canResize: PropTypes.oneOf([true, false, `left`, `right`, `both`]),
    useResizeHandle: PropTypes.bool,
    canSelect: PropTypes.bool,

    stackItems: PropTypes.bool,
    useLayers: PropTypes.bool,

    itemTouchSendsClick: PropTypes.bool,

    horizontalLineClassNamesForGroup: PropTypes.func,

    onItemMove: PropTypes.func,
    onItemResize: PropTypes.func,
    onItemClick: PropTypes.func,
    onItemSelect: PropTypes.func,
    onItemDeselect: PropTypes.func,
    onCanvasClick: PropTypes.func,
    onItemDoubleClick: PropTypes.func,
    onItemContextMenu: PropTypes.func,
    onCanvasDoubleClick: PropTypes.func,
    onCanvasContextMenu: PropTypes.func,
    onZoom: PropTypes.func,
    onRowMouseMove: PropTypes.func,
    onRowHover: PropTypes.func,
    onRowMouseDown: PropTypes.func,
    onRowMouseUp: PropTypes.func,
    onCanvasMouseDown: PropTypes.func,
    onCanvasMouseUp: PropTypes.func,
    onCanvasMouseMove: PropTypes.func,
    onCanvasHover: PropTypes.func,

    moveResizeValidator: PropTypes.func,

    itemRenderer: PropTypes.func,
    groupRenderer: PropTypes.func,

    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types

    keys: PropTypes.shape({
      groupIdKey: PropTypes.string,
      groupTitleKey: PropTypes.string,
      groupRightTitleKey: PropTypes.string,
      itemIdKey: PropTypes.string,
      itemTitleKey: PropTypes.string,
      itemDivTitleKey: PropTypes.string,
      itemGroupKey: PropTypes.string,
      itemTimeStartKey: PropTypes.string,
      itemTimeEndKey: PropTypes.string,
      itemLayerKey: PropTypes.string,
    }),
    headerRef: PropTypes.func,
    scrollRef: PropTypes.func,

    timeSteps: PropTypes.shape({
      second: PropTypes.number,
      minute: PropTypes.number,
      hour: PropTypes.number,
      day: PropTypes.number,
      month: PropTypes.number,
      year: PropTypes.number,
    }),

    defaultTimeStart: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    defaultTimeEnd: PropTypes.object, // eslint-disable-line react/forbid-prop-types

    visibleTimeStart: PropTypes.number,
    visibleTimeEnd: PropTypes.number,
    onTimeChange: PropTypes.func,
    onBoundsChange: PropTypes.func,

    // TODO выпилить этот функционал, селекнутость должна задаваться извне
    selected: PropTypes.array, // eslint-disable-line react/forbid-prop-types

    replacementHeightTop: PropTypes.number,
    replacementHeightBottom: PropTypes.number,

    headerLabelFormats: PropTypes.shape({
      yearShort: PropTypes.string,
      yearLong: PropTypes.string,
      monthShort: PropTypes.string,
      monthMedium: PropTypes.string,
      monthMediumLong: PropTypes.string,
      monthLong: PropTypes.string,
      dayShort: PropTypes.string,
      dayLong: PropTypes.string,
      hourShort: PropTypes.string,
      hourMedium: PropTypes.string,
      hourMediumLong: PropTypes.string,
      hourLong: PropTypes.string,
    }),

    subHeaderLabelFormats: PropTypes.shape({
      yearShort: PropTypes.string,
      yearLong: PropTypes.string,
      monthShort: PropTypes.string,
      monthMedium: PropTypes.string,
      monthLong: PropTypes.string,
      dayShort: PropTypes.string,
      dayMedium: PropTypes.string,
      dayMediumLong: PropTypes.string,
      dayLong: PropTypes.string,
      hourShort: PropTypes.string,
      hourLong: PropTypes.string,
      minuteShort: PropTypes.string,
      minuteLong: PropTypes.string,
    }),

    resizeDetector: PropTypes.shape({
      addListener: PropTypes.func,
      removeListener: PropTypes.func,
    }),

    verticalLineClassNamesForTime: PropTypes.func,

    children: PropTypes.node,
    planStart: PropTypes.number,
    planEnd: PropTypes.number,
  }

  static childContextTypes = {
    getTimelineContext: PropTypes.func,
  }

  static defaultProps = {
    sidebarWidth: 150,
    rightSidebarWidth: 0,
    dragSnap: 1000 * 60 * 15, // 15min
    minResizeWidth: 20,
    stickyOffset: 0,
    stickyHeader: true,
    lineHeight: 30,
    headerLabelGroupHeight: 30,
    headerLabelHeight: 30,
    itemHeightRatio: 0.65,

    minZoom: 60 * 60 * 1000, // 1 hour
    maxZoom: 5 * 365.24 * 86400 * 1000, // 5 years

    clickTolerance: 3, // how many pixels can we drag for it to be still considered a click?

    canChangeGroup: true,
    canMove: true,
    canResize: `right`,
    useResizeHandle: false,
    canSelect: true,

    stackItems: false,
    useLayers: false,

    horizontalLineClassNamesForGroup: null,

    onItemMove: null,
    onItemResize: null,
    onItemClick: null,
    onItemSelect: null,
    onItemDeselect: null,
    onCanvasClick: null,
    onItemDoubleClick: null,
    onItemContextMenu: null,
    onZoom: null,
    onRowMouseMove: null,
    onRowHover: null,
    onRowMouseUp: null,
    onRowMouseDown: null,
    onCanvasMouseDown: null,
    onCanvasMouseUp: null,
    onCanvasMouseMove: null,
    onCanvasHover: null,

    verticalLineClassNamesForTime: null,

    moveResizeValidator: null,

    defaultTimeStart: null,
    defaultTimeEnd: null,

    itemTouchSendsClick: false,

    style: {},
    keys: defaultKeys,
    timeSteps: defaultTimeSteps,
    headerRef: () => {},
    scrollRef: () => {},

    // if you pass in visibleTimeStart and visibleTimeEnd, you must also pass onTimeChange(visibleTimeStart, visibleTimeEnd),
    // which needs to update the props visibleTimeStart and visibleTimeEnd to the ones passed
    visibleTimeStart: null,
    visibleTimeEnd: null,
    onTimeChange(visibleTimeStart, visibleTimeEnd, updateScrollCanvas) {
      updateScrollCanvas(visibleTimeStart, visibleTimeEnd)
    },
    // called when the canvas area of the calendar changes
    onBoundsChange: null,
    children: null,

    replacementHeightTop: 0,
    replacementHeightBottom: 0,

    headerLabelFormats: defaultHeaderLabelFormats,
    subHeaderLabelFormats: defaultSubHeaderLabelFormats,

    selected: null,
    sidebarContent: null,
    rightSidebarContent: null,
    onCanvasDoubleClick: null,
    onCanvasContextMenu: null,
    itemRenderer: undefined,
    groupRenderer: null,
    resizeDetector: null,
    planStart: undefined,
    planEnd: undefined,
  }

  constructor(props) {
    super(props)

    const {
      defaultTimeStart,
      defaultTimeEnd,
      visibleTimeStart: visibleTimeStartProp,
      visibleTimeEnd: visibleTimeEndProp,
    } = this.props

    let visibleTimeStart = null
    let visibleTimeEnd = null

    if (defaultTimeStart && defaultTimeEnd) {
      visibleTimeStart = defaultTimeStart.valueOf()
      visibleTimeEnd = defaultTimeEnd.valueOf()
    } else if (visibleTimeStartProp && visibleTimeEndProp) {
      visibleTimeStart = visibleTimeStartProp
      visibleTimeEnd = visibleTimeEndProp
    } else {
      // throwing an error because neither default or visible time props provided
      throw new Error(
        `You must provide either "defaultTimeStart" and "defaultTimeEnd" or "visibleTimeStart" and "visibleTimeEnd" to initialize the Timeline`,
      )
    }

    this.state = {
      width: 1000,

      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart: visibleTimeStart - (visibleTimeEnd - visibleTimeStart),

      selectedItem: null,
      dragTime: null,
      dragGroupTitle: null,
      resizeTime: null,
      topOffset: 0,
      resizingItem: null,
      resizingEdge: null,
    }

    const {
      canvasTimeStart,
      visibleTimeStart: visibleTimeStartState,
      visibleTimeEnd: visibleTimeEndState,
      width,
    } = this.state

    const { dimensionItems, height, groupHeights, groupTops } = this.stackItems(
      props.items,
      props.groups,
      canvasTimeStart,
      visibleTimeStartState,
      visibleTimeEndState,
      width,
    )

    /* eslint-disable react/no-direct-mutation-state */
    this.state.dimensionItems = dimensionItems
    this.state.height = height
    this.state.groupHeights = groupHeights
    this.state.groupTops = groupTops
    /* eslint-enable react/no-direct-mutation-state */
  }

  getChildContext() {
    return {
      getTimelineContext: () => this.getTimelineContext(),
    }
  }

  componentDidMount() {
    this.resize(this.props)

    const { resizeDetector } = this.props

    if (resizeDetector && resizeDetector.addListener) {
      resizeDetector.addListener(this)
    }

    windowResizeDetector.addListener(this)

    this.lastTouchDistance = null
  }

  // eslint-disable-next-line camelcase, react/sort-comp
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { visibleTimeStart, visibleTimeEnd, items, groups } = nextProps
    const { items: itemsProp, groups: groupsProp } = this.props

    if (visibleTimeStart && visibleTimeEnd) {
      this.updateScrollCanvas(
        visibleTimeStart,
        visibleTimeEnd,
        items !== itemsProp || groups !== groupsProp,
        items,
        groups,
      )
    } else if (items !== itemsProp || groups !== groupsProp) {
      this.updateDimensions(items, groups)
    }

    // resize timeline to current window size
    if (items && groups) {
      this.resize(nextProps)
    }
  }

  componentWillUnmount() {
    const { resizeDetector } = this.props

    if (resizeDetector && resizeDetector.addListener) {
      resizeDetector.removeListener(this)
    }

    windowResizeDetector.removeListener(this)
  }

  getTimelineContext = () => {
    const {
      width,
      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart,
    } = this.state
    const zoom = visibleTimeEnd - visibleTimeStart
    const canvasTimeEnd = canvasTimeStart + zoom * 3

    return {
      timelineWidth: width,
      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart,
      canvasTimeEnd,
    }
  }

  resize = (props = this.props) => {
    const {
      width: containerWidth,
      top: containerTop,
    } = this.container.getBoundingClientRect()

    const width = containerWidth - props.sidebarWidth - props.rightSidebarWidth
    const { headerLabelGroupHeight, headerLabelHeight } = props
    const headerHeight = headerLabelGroupHeight + headerLabelHeight

    const { canvasTimeStart, visibleTimeStart, visibleTimeEnd } = this.state

    const { dimensionItems, height, groupHeights, groupTops } = this.stackItems(
      props.items,
      props.groups,
      canvasTimeStart,
      visibleTimeStart,
      visibleTimeEnd,
      width,
    )

    // this is needed by dragItem since it uses pageY from the drag events
    // if this was in the context of the scrollElement, this would not be necessary
    const topOffset = containerTop + window.pageYOffset + headerHeight

    this.setState({
      width,
      topOffset,
      dimensionItems,
      height,
      groupHeights,
      groupTops,
    })
    this.scrollComponent.scrollLeft = width
    this.headerScrollListener(width)
  }

  onScroll = scrollX => {
    const { onTimeChange } = this.props
    const {
      canvasTimeStart,
      visibleTimeEnd,
      visibleTimeStart: visibleTimeStartState,
      width,
    } = this.state

    const zoom = visibleTimeEnd - visibleTimeStartState
    const visibleTimeStart = canvasTimeStart + (zoom * scrollX) / width
    if (scrollX < width * 0.5) {
      this.setState({
        canvasTimeStart: canvasTimeStart - zoom,
      })
    }
    if (scrollX > width * 1.5) {
      this.setState({
        canvasTimeStart: canvasTimeStart + zoom,
      })
    }

    if (
      visibleTimeStartState !== visibleTimeStart ||
      visibleTimeEnd !== visibleTimeStart + zoom
    ) {
      onTimeChange(visibleTimeStart, visibleTimeStart + zoom, (start, end) => {
        let nextHeaderScrollX = scrollX
        const scrollLimitReached = start !== visibleTimeStart
        const scrollDirection =
          visibleTimeStart > visibleTimeStartState ? `asc` : `desc`

        // Adjust next header scroll offset near scroll borders to avoid "bumping"
        if (scrollDirection === `desc` && scrollLimitReached) {
          nextHeaderScrollX = scrollX >= width ? scrollX : width
        }

        if (scrollDirection === `asc` && scrollLimitReached) {
          nextHeaderScrollX = scrollX <= width ? scrollX : width
        }

        // Fix blinking when virtual canvas are moved
        if (scrollX < width * 0.5) {
          nextHeaderScrollX = width
        }
        if (scrollX > width * 1.5) {
          nextHeaderScrollX = width
        }

        this.updateScrollCanvas(start, end)
        this.headerScrollListener(nextHeaderScrollX)
      })
    }
  }

  // called when the visible time changes
  updateScrollCanvas = (
    visibleTimeStart,
    visibleTimeEnd,
    forceUpdateDimensions,
    updatedItems,
    updatedGroups,
  ) => {
    const {
      canvasTimeStart,
      visibleTimeStart: visibleTimeStartState,
      visibleTimeEnd: visibleTimeEndState,
      width,
    } = this.state

    const {
      onBoundsChange,
      onZoom,
      items: itemsProp,
      groups: groupsProp,
    } = this.props

    const oldCanvasTimeStart = canvasTimeStart
    const oldZoom = visibleTimeEndState - visibleTimeStartState
    const newZoom = visibleTimeEnd - visibleTimeStart
    const items = updatedItems || itemsProp
    const groups = updatedGroups || groupsProp

    const newState = {
      visibleTimeStart,
      visibleTimeEnd,
    }

    let resetCanvas = false

    const canKeepCanvas =
      visibleTimeStart >= oldCanvasTimeStart + oldZoom * 0.5 &&
      visibleTimeStart <= oldCanvasTimeStart + oldZoom * 1.5 &&
      visibleTimeEnd >= oldCanvasTimeStart + oldZoom * 1.5 &&
      visibleTimeEnd <= oldCanvasTimeStart + oldZoom * 2.5

    // if new visible time is in the right canvas area
    if (canKeepCanvas) {
      // but we need to update the scroll
      const newScrollLeft = Math.round(
        (width * (visibleTimeStart - oldCanvasTimeStart)) / newZoom,
      )
      if (this.scrollComponent.scrollLeft !== newScrollLeft) {
        resetCanvas = true
      }
    } else {
      resetCanvas = true
    }

    if (resetCanvas) {
      newState.canvasTimeStart = visibleTimeStart - newZoom
      this.scrollComponent.scrollLeft = width

      if (onBoundsChange) {
        onBoundsChange(
          newState.canvasTimeStart,
          newState.canvasTimeStart + newZoom * 3,
        )
      }
    }

    if (resetCanvas || forceUpdateDimensions) {
      const canvasTimeStart = newState.canvasTimeStart
        ? newState.canvasTimeStart
        : oldCanvasTimeStart
      const {
        dimensionItems,
        height,
        groupHeights,
        groupTops,
      } = this.stackItems(
        items,
        groups,
        canvasTimeStart,
        visibleTimeStart,
        visibleTimeEnd,
        width,
      )
      newState.dimensionItems = dimensionItems
      newState.height = height
      newState.groupHeights = groupHeights
      newState.groupTops = groupTops
    }

    this.setState(newState, () => {
      // are we changing zoom? Well then let's report it
      // need to wait until state is set so that we get current
      // timeline context
      if (onZoom && oldZoom !== newZoom) {
        onZoom(this.getTimelineContext())
      }
    })
  }

  handleWheelZoom = (speed, xPosition, deltaY) => {
    const { width } = this.state

    this.changeZoom(1.0 + (speed * deltaY) / 500, xPosition / width)
  }

  changeZoom = (scale, offset = 0.5) => {
    const { minZoom, maxZoom, onTimeChange } = this.props
    const { visibleTimeStart, visibleTimeEnd } = this.state

    const oldZoom = visibleTimeEnd - visibleTimeStart
    const newZoom = Math.min(
      Math.max(Math.round(oldZoom * scale), minZoom),
      maxZoom,
    ) // min 1 min, max 20 years
    const newVisibleTimeStart = Math.round(
      visibleTimeStart + (oldZoom - newZoom) * offset,
    )

    onTimeChange(
      newVisibleTimeStart,
      newVisibleTimeStart + newZoom,
      (start, end) => {
        this.updateScrollCanvas(start, end)
        this.headerScrollListener(this.scrollComponent.scrollLeft)
      },
    )
  }

  showPeriod = (from, unit) => {
    let visibleTimeStart = from.valueOf()
    let visibleTimeEnd = moment(from)
      .add(1, unit)
      .valueOf()
    let zoom = visibleTimeEnd - visibleTimeStart

    // can't zoom in more than to show one hour
    if (zoom < 360000) {
      return
    }

    const {
      visibleTimeStart: visibleTimeStartState,
      visibleTimeEnd: visibleTimeEndState,
    } = this.state

    const { maxZoom, onTimeChange } = this.props

    // clicked on the big header and already focused here, zoom out
    if (
      unit !== `year` &&
      visibleTimeEndState - visibleTimeStartState <= zoom
    ) {
      const nextUnit = getNextUnit(unit, maxZoom)

      visibleTimeStart = from.startOf(nextUnit).valueOf()
      visibleTimeEnd = moment(visibleTimeStart).add(1, nextUnit)
      visibleTimeEnd.subtract(1, `millisecond`)
      zoom = visibleTimeEnd - visibleTimeStart
    }

    onTimeChange(visibleTimeStart, visibleTimeStart + zoom, (start, end) => {
      this.updateScrollCanvas(start, end)
      this.headerScrollListener(this.scrollComponent.scrollLeft)
    })
  }

  selectItem = (item, clickType, e) => {
    const { selectedItem } = this.state
    const {
      itemTouchSendsClick,
      onItemClick,
      onItemSelect,
      onItemDeselect,
    } = this.props

    if (
      selectedItem === item ||
      (itemTouchSendsClick && clickType === `touch`)
    ) {
      if (item && onItemClick) {
        const time = this.getTimeFromMouseEvent(e)
        onItemClick(item, e, time)
      }
    } else {
      this.setState({ selectedItem: item })
      if (item && onItemSelect) {
        const time = this.getTimeFromMouseEvent(e)
        onItemSelect(item, e, time)
      } else if (item === null && onItemDeselect) {
        onItemDeselect(e) // this isnt in the docs. Is this function even used?
      }
    }
  }

  doubleClickItem = (item, e) => {
    const { onItemDoubleClick } = this.props

    if (onItemDoubleClick) {
      const time = this.getTimeFromMouseEvent(e)
      onItemDoubleClick(item, e, time)
    }
  }

  contextMenuClickItem = (item, e) => {
    const { onItemContextMenu } = this.props

    if (onItemContextMenu) {
      const time = this.getTimeFromMouseEvent(e)
      onItemContextMenu(item, e, time)
    }
  }

  getTimeFromMouseEvent = e => {
    const { width, visibleTimeStart, visibleTimeEnd } = this.state
    const { dragSnap } = this.props

    const { scrollComponent } = this
    const { left: scrollX } = scrollComponent.getBoundingClientRect()

    const xRelativeToTimeline = e.clientX - scrollX

    const relativeItemPosition = xRelativeToTimeline / width
    const zoom = visibleTimeEnd - visibleTimeStart
    const timeOffset = relativeItemPosition * zoom

    let time = Math.round(visibleTimeStart + timeOffset)
    time = Math.floor(time / dragSnap) * dragSnap

    return time
  }

  dragItem = (item, dragTime, newGroupOrder) => {
    const { groups } = this.props

    const newGroup = groups[newGroupOrder]
    const { keys } = this.props

    this.setState({
      draggingItem: item,
      dragTime,
      newGroupOrder,
      dragGroupTitle: newGroup ? _get(newGroup, keys.groupTitleKey) : ``,
    })
  }

  dropItem = (item, dragTime, newGroupOrder) => {
    const { onItemMove } = this.props

    this.setState({ draggingItem: null, dragTime: null, dragGroupTitle: null })
    if (onItemMove) {
      onItemMove(item, dragTime, newGroupOrder)
    }
  }

  resizingItem = (item, resizeTime, edge) => {
    this.setState({
      resizingItem: item,
      resizingEdge: edge,
      resizeTime,
    })
  }

  resizedItem = (item, resizeTime, edge, timeDelta) => {
    const { onItemResize } = this.props

    this.setState({ resizingItem: null, resizingEdge: null, resizeTime: null })
    if (onItemResize && timeDelta !== 0) {
      onItemResize(item, resizeTime, edge)
    }
  }

  handleRowClick = (e, rowIndex, layer) => {
    const { selectedItem } = this.state
    const { onCanvasClick, groups, keys } = this.props

    // shouldnt this be handled by the user, as far as when to deselect an item?
    if (selectedItem) {
      this.selectItem(null)
    }

    if (onCanvasClick == null) return

    const time = this.getTimeFromMouseEvent(e)
    const groupId = _get(groups[rowIndex], keys.groupIdKey)
    onCanvasClick(groupId, time, e, layer)
  }

  handleRowDoubleClick = (e, rowIndex, layer) => {
    const { onCanvasDoubleClick, groups, keys } = this.props

    if (onCanvasDoubleClick == null) return

    const time = this.getTimeFromMouseEvent(e)
    const groupId = _get(groups[rowIndex], keys.groupIdKey)
    onCanvasDoubleClick(groupId, time, e, layer)
  }

  handleRowMouseMove = (e, rowIndex, layer) => {
    const { onRowMouseMove } = this.props

    if (onRowMouseMove == null) return

    const time = this.getTimeFromMouseEvent(e)

    onRowMouseMove(rowIndex, time, layer)
  }

  handleRowMouseOver = (e, rowIndex, layer) => {
    const { onRowHover } = this.props

    if (onRowHover == null) return
    onRowHover(rowIndex, true, layer)
  }

  handleRowMouseOut = (e, rowIndex, layer) => {
    const { onRowHover } = this.props

    if (onRowHover == null) return
    onRowHover(rowIndex, false, layer)
  }

  handleCanvasMouseEnter = e => {
    const { onCanvasHover } = this.props

    if (onCanvasHover == null) return

    // Ignore fake events from Item.js
    if (e.relatedTarget !== window) onCanvasHover(true)
  }

  handleCanvasMouseLeave = e => {
    const { onCanvasHover } = this.props

    if (onCanvasHover == null) return

    // Ignore fake events from Item.js
    if (e.relatedTarget !== window) onCanvasHover(false)
  }

  handleCanvasMouseMove = e => {
    const { onCanvasMouseMove } = this.props

    if (onCanvasMouseMove == null) return

    const time = this.getTimeFromMouseEvent(e)

    onCanvasMouseMove(time)
  }

  handleCanvasMouseDown = e => {
    const { onCanvasMouseDown } = this.props

    if (onCanvasMouseDown == null) return
    onCanvasMouseDown(this.getTimeFromMouseEvent(e))
  }

  handleCanvasMouseUp = e => {
    const { onCanvasMouseUp } = this.props

    if (onCanvasMouseUp == null) return
    onCanvasMouseUp(this.getTimeFromMouseEvent(e))
  }

  handleRowMouseDown = (e, rowIndex, layer) => {
    const { onRowMouseDown } = this.props

    if (onRowMouseDown == null) return

    const time = this.getTimeFromMouseEvent(e)

    onRowMouseDown(rowIndex, time, layer)
  }

  handleRowMouseUp = (e, rowIndex, layer) => {
    const { onRowMouseUp } = this.props

    if (onRowMouseUp == null) return

    const time = this.getTimeFromMouseEvent(e)

    onRowMouseUp(rowIndex, time, layer)
  }

  handleScrollContextMenu = (e, rowIndex, layer) => {
    const { onCanvasContextMenu, groups, keys } = this.props

    if (onCanvasContextMenu == null) return

    const timePosition = this.getTimeFromMouseEvent(e)

    const groupId = _get(groups[rowIndex], keys.groupIdKey)

    if (onCanvasContextMenu) {
      e.preventDefault()
      onCanvasContextMenu(groupId, timePosition, e, layer)
    }
  }

  registerScrollListener = listener => {
    this.headerScrollListener = listener
  }

  columns(
    canvasTimeStart,
    canvasTimeEnd,
    canvasWidth,
    minUnit,
    timeSteps,
    height,
  ) {
    const {
      groups,
      verticalLineClassNamesForTime,
      planStart,
      planEnd,
    } = this.props

    return (
      <Columns
        canvasTimeStart={canvasTimeStart}
        canvasTimeEnd={canvasTimeEnd}
        canvasWidth={canvasWidth}
        lineCount={_length(groups)}
        minUnit={minUnit}
        timeSteps={timeSteps}
        height={height}
        verticalLineClassNamesForTime={verticalLineClassNamesForTime}
        planStart={planStart}
        planEnd={planEnd}
      />
    )
  }

  updateDimensions(items, groups) {
    const {
      canvasTimeStart,
      visibleTimeStart,
      visibleTimeEnd,
      width,
    } = this.state
    const { dimensionItems, height, groupHeights, groupTops } = this.stackItems(
      items,
      groups,
      canvasTimeStart,
      visibleTimeStart,
      visibleTimeEnd,
      width,
    )

    this.setState({ dimensionItems, height, groupHeights, groupTops })
  }

  rows(
    canvasWidth,
    groupHeights,
    groups,
    lineHeight,
    hoverIndex,
    setHoverIndex,
  ) {
    const {
      clickTolerance,
      horizontalLineClassNamesForGroup,
      groups: groupsProp,
    } = this.props

    return (
      <GroupRows
        groups={groups}
        canvasWidth={canvasWidth}
        lineCount={_length(groupsProp)}
        groupHeights={groupHeights}
        clickTolerance={clickTolerance}
        onRowClick={this.handleRowClick}
        onRowMouseMove={this.handleRowMouseMove}
        onRowMouseOver={this.handleRowMouseOver}
        onRowMouseOut={this.handleRowMouseOut}
        onRowDoubleClick={this.handleRowDoubleClick}
        horizontalLineClassNamesForGroup={horizontalLineClassNamesForGroup}
        onRowContextClick={this.handleScrollContextMenu}
        onRowMouseUp={this.handleRowMouseUp}
        onRowMouseDown={this.handleRowMouseDown}
        lineHeight={lineHeight}
        hoverIndex={hoverIndex}
        setHoverIndex={setHoverIndex}
      />
    )
  }

  items(
    canvasTimeStart,
    zoom,
    canvasTimeEnd,
    canvasWidth,
    minUnit,
    dimensionItems,
    groupHeights,
    groupTops,
  ) {
    const {
      items,
      groups,
      keys,
      dragSnap,
      minResizeWidth,
      canChangeGroup,
      canMove,
      canResize,
      useResizeHandle,
      canSelect,
      moveResizeValidator,
      itemRenderer,
      selected,
    } = this.props

    const { selectedItem, topOffset } = this.state

    return (
      <Items
        canvasTimeStart={canvasTimeStart}
        canvasTimeEnd={canvasTimeEnd}
        canvasWidth={canvasWidth}
        dimensionItems={dimensionItems}
        groupTops={groupTops}
        items={items}
        groups={groups}
        keys={keys}
        selectedItem={selectedItem}
        dragSnap={dragSnap}
        minResizeWidth={minResizeWidth}
        canChangeGroup={canChangeGroup}
        canMove={canMove}
        canResize={canResize}
        useResizeHandle={useResizeHandle}
        canSelect={canSelect}
        moveResizeValidator={moveResizeValidator}
        topOffset={topOffset}
        itemSelect={this.selectItem}
        itemDrag={this.dragItem}
        itemDrop={this.dropItem}
        onItemDoubleClick={this.doubleClickItem}
        onItemContextMenu={this.contextMenuClickItem}
        itemResizing={this.resizingItem}
        itemResized={this.resizedItem}
        itemRenderer={itemRenderer}
        selected={selected}
      />
    )
  }

  infoLabel() {
    let label = null

    const { dragTime, dragGroupTitle, resizeTime } = this.state

    if (dragTime) {
      label = `${moment(dragTime).format(`LLL`)}, ${dragGroupTitle}`
    } else if (resizeTime) {
      label = moment(resizeTime).format(`LLL`)
    }

    return label ? <InfoLabel label={label} /> : ``
  }

  header(
    canvasTimeStart,
    canvasTimeEnd,
    canvasWidth,
    minUnit,
    timeSteps,
    headerLabelGroupHeight,
    headerLabelHeight,
  ) {
    const {
      sidebarWidth,
      rightSidebarWidth,
      sidebarContent,
      rightSidebarContent,
      stickyOffset,
      stickyHeader,
      headerLabelFormats,
      subHeaderLabelFormats,
      headerRef,
      planStart,
      planEnd,
    } = this.props

    const leftSidebar = sidebarWidth != null && sidebarWidth > 0 && (
      <div className="rct-sidebar-header" style={{ width: sidebarWidth }}>
        {sidebarContent}
      </div>
    )

    const rightSidebar = rightSidebarWidth != null && rightSidebarWidth > 0 && (
      <div
        className="rct-sidebar-header rct-sidebar-right"
        style={{ width: rightSidebarWidth }}
      >
        {rightSidebarContent}
      </div>
    )

    const { width } = this.state

    return (
      <Header
        canvasTimeStart={canvasTimeStart}
        hasRightSidebar={rightSidebarWidth > 0}
        canvasTimeEnd={canvasTimeEnd}
        canvasWidth={canvasWidth}
        minUnit={minUnit}
        timeSteps={timeSteps}
        headerLabelGroupHeight={headerLabelGroupHeight}
        headerLabelHeight={headerLabelHeight}
        width={width}
        stickyOffset={stickyOffset}
        stickyHeader={stickyHeader}
        showPeriod={this.showPeriod}
        headerLabelFormats={headerLabelFormats}
        subHeaderLabelFormats={subHeaderLabelFormats}
        registerScroll={this.registerScrollListener}
        leftSidebarHeader={leftSidebar}
        rightSidebarHeader={rightSidebar}
        headerRef={headerRef}
        planStart={planStart}
        planEnd={planEnd}
      />
    )
  }

  sidebar(height, groupHeights) {
    const { sidebarWidth, groups, groupRenderer, keys } = this.props

    return (
      sidebarWidth != null &&
      sidebarWidth > 0 && (
        <Sidebar
          groups={groups}
          groupRenderer={groupRenderer}
          keys={keys}
          width={sidebarWidth}
          groupHeights={groupHeights}
          height={height}
        />
      )
    )
  }

  rightSidebar(height, groupHeights) {
    const { rightSidebarWidth, groups, keys, groupRenderer } = this.props

    return (
      rightSidebarWidth != null &&
      rightSidebarWidth > 0 && (
        <Sidebar
          groups={groups}
          keys={keys}
          groupRenderer={groupRenderer}
          isRightSidebar
          width={rightSidebarWidth}
          groupHeights={groupHeights}
          height={height}
        />
      )
    )
  }

  stackItems(
    items,
    groups,
    canvasTimeStart,
    visibleTimeStart,
    visibleTimeEnd,
    width,
  ) {
    // if there are no groups return an empty array of dimensions
    if (groups.length === 0) {
      return {
        dimensionItems: [],
        height: 0,
        groupHeights: [],
        groupTops: [],
      }
    }

    const {
      keys,
      lineHeight,
      stackItems,
      itemHeightRatio,
      useLayers,
      replacementHeightTop,
      replacementHeightBottom,
    } = this.props
    const {
      draggingItem,
      dragTime,
      resizingItem,
      resizingEdge,
      resizeTime,
      newGroupOrder,
    } = this.state
    const zoom = visibleTimeEnd - visibleTimeStart
    const canvasTimeEnd = canvasTimeStart + zoom * 3
    const canvasWidth = width * 3

    const visibleItems = getVisibleItems(
      items,
      canvasTimeStart,
      canvasTimeEnd,
      keys,
    )
    const groupOrders = getGroupOrders(groups, keys)

    const dimensionItems = visibleItems.reduce((memo, item) => {
      const itemId = _get(item, keys.itemIdKey)
      const itemLayer = _get(item, keys.itemLayerKey) || 0
      const isDragging = itemId === draggingItem
      const isResizing = itemId === resizingItem

      const dimension = calculateDimensions({
        itemTimeStart: _get(item, keys.itemTimeStartKey),
        itemTimeEnd: _get(item, keys.itemTimeEndKey),
        isDragging,
        isResizing,
        canvasTimeStart,
        canvasTimeEnd,
        canvasWidth,
        dragTime,
        resizingEdge,
        resizeTime,
      })

      if (dimension) {
        dimension.top = null
        dimension.order = isDragging
          ? newGroupOrder
          : groupOrders[_get(item, keys.itemGroupKey)]
        dimension.stack = !item.isOverlay
        dimension.height = lineHeight * itemHeightRatio
        dimension.isDragging = isDragging

        memo.push({
          id: itemId,
          layer: itemLayer,
          dimensions: dimension,
        })
      }

      return memo
    }, [])

    const getStackFunction = (stackItems, useLayers) => {
      if (stackItems) {
        if (useLayers) {
          return layers
        }
        return stack
      }
      return nostack
    }

    const stackingMethod = getStackFunction(stackItems, useLayers)

    // TODO по итогу вызывается слишком часто; надо всё оптимизировать, чтобы вызывалось реже
    const { height, groupHeights, groupTops } = stackingMethod({
      groupOrders,
      lineHeight,
      groups,
      keys,
      items: dimensionItems,
    })

    return {
      dimensionItems,
      height,
      outerHeight: height + replacementHeightTop + replacementHeightBottom,
      groupHeights,
      groupTops,
    }
  }

  childrenWithProps(
    canvasTimeStart,
    canvasTimeEnd,
    canvasWidth,
    dimensionItems,
    groupHeights,
    groupTops,
    height,
    headerHeight,
    visibleTimeStart,
    visibleTimeEnd,
    minUnit,
    timeSteps,
  ) {
    const { children, items, groups, keys, selected } = this.props
    const { selectedItem } = this.state

    if (!children) {
      return null
    }

    // convert to an array and remove the nulls
    const childArray = Array.isArray(children)
      ? children.filter(c => c)
      : [children]

    const childProps = {
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
      visibleTimeStart,
      visibleTimeEnd,
      dimensionItems,
      items,
      groups,
      keys,
      groupHeights,
      groupTops,
      selected: selectedItem && !selected ? [selectedItem] : selected || [],
      height,
      headerHeight,
      minUnit,
      timeSteps,
    }

    return React.Children.map(childArray, child =>
      React.cloneElement(child, childProps),
    )
  }

  render() {
    const {
      items,
      groups,
      headerLabelGroupHeight,
      headerLabelHeight,
      sidebarWidth,
      rightSidebarWidth,
      timeSteps,
      replacementHeightTop,
      replacementHeightBottom,
      lineHeight,
      style,
      scrollRef,
      hoverIndex,
      setHoverIndex,
    } = this.props
    const {
      draggingItem,
      resizingItem,
      width,
      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart,
    } = this.state
    let { dimensionItems, height, groupHeights, groupTops } = this.state

    const zoom = visibleTimeEnd - visibleTimeStart
    const canvasTimeEnd = canvasTimeStart + zoom * 3
    const canvasWidth = width * 3
    const minUnit = getMinUnit(zoom, width, timeSteps)
    const headerHeight = headerLabelGroupHeight + headerLabelHeight

    const isInteractingWithItem = !!draggingItem || !!resizingItem

    if (isInteractingWithItem) {
      ;({ dimensionItems, height, groupHeights, groupTops } = this.stackItems(
        items,
        groups,
        canvasTimeStart,
        visibleTimeStart,
        visibleTimeEnd,
        width,
      ))
    }

    const outerComponentStyle = {
      paddingTop: `${replacementHeightTop}px`,
      paddingBottom: `${replacementHeightBottom}px`,
      height: `${height + replacementHeightTop + replacementHeightBottom}px`,
    }

    return (
      <TimelineStateProvider
        visibleTimeStart={visibleTimeStart}
        visibleTimeEnd={visibleTimeEnd}
        canvasTimeStart={canvasTimeStart}
        canvasTimeEnd={canvasTimeEnd}
        canvasWidth={canvasWidth}
      >
        <TimelineMarkersProvider>
          <div
            style={style}
            ref={el => {
              this.container = el
              return el
            }}
            className="react-calendar-timeline"
          >
            {this.header(
              canvasTimeStart,
              canvasTimeEnd,
              canvasWidth,
              minUnit,
              timeSteps,
              headerLabelGroupHeight,
              headerLabelHeight,
            )}
            <div style={outerComponentStyle} className="rct-outer">
              {sidebarWidth > 0 ? this.sidebar(height, groupHeights) : null}
              <ScrollElement
                scrollRef={el => {
                  scrollRef(el)
                  this.scrollComponent = el
                }}
                width={width}
                height={height}
                onZoom={this.changeZoom}
                onWheelZoom={this.handleWheelZoom}
                onScroll={this.onScroll}
                isInteractingWithItem={isInteractingWithItem}
                onMouseDown={this.handleCanvasMouseDown}
                onMouseUp={this.handleCanvasMouseUp}
                onMouseEnter={this.handleCanvasMouseEnter}
                onMouseLeave={this.handleCanvasMouseLeave}
                onMouseMove={this.handleCanvasMouseMove}
              >
                <MarkerCanvas>
                  {this.items(
                    canvasTimeStart,
                    zoom,
                    canvasTimeEnd,
                    canvasWidth,
                    minUnit,
                    dimensionItems,
                    groupHeights,
                    groupTops,
                  )}
                  {this.columns(
                    canvasTimeStart,
                    canvasTimeEnd,
                    canvasWidth,
                    minUnit,
                    timeSteps,
                    height,
                    headerHeight,
                  )}
                  {this.rows(
                    canvasWidth,
                    groupHeights,
                    groups,
                    lineHeight,
                    hoverIndex,
                    setHoverIndex,
                  )}
                  {this.infoLabel()}
                  {this.childrenWithProps(
                    canvasTimeStart,
                    canvasTimeEnd,
                    canvasWidth,
                    dimensionItems,
                    groupHeights,
                    groupTops,
                    height,
                    headerHeight,
                    visibleTimeStart,
                    visibleTimeEnd,
                    minUnit,
                    timeSteps,
                  )}
                </MarkerCanvas>
              </ScrollElement>
              {rightSidebarWidth > 0
                ? this.rightSidebar(height, groupHeights)
                : null}
            </div>
          </div>
        </TimelineMarkersProvider>
      </TimelineStateProvider>
    )
  }
}
